import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';
import { ReceiveSupplierCell, ReceiveSupplierHeader } from '~/components/receive';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  RECEIVE_REQUEST_STATUS,
  RECEIVE_REQUEST_STATUS_BADGE_COLOR,
  RELEASE_CODE,
  type TypeReceiveRequestStatus,
} from '~/constants';

import { formatReceiveExpectedDateTime } from '../formatReceiveExpectedDateTime';

import type { TypeReceiveRequestsInfo } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 페이지 테이블
 */
const getReceiveRegisterColumnDefs = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (data?: TypeReceiveRequestsInfo) => void;
}) => {
  const columnDefs: ColDef<TypeReceiveRequestsInfo>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 72,
    },
    {
      field: 'requestCode',
      headerName: '입고요청번호',
      width: 180,
    },
    {
      field: 'status',
      headerName: '요청상태',
      width: 100,
      minWidth: 100,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'start' },
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['status']>) => ({
        variant: 'light',
        status: true,
        color: RECEIVE_REQUEST_STATUS_BADGE_COLOR[value as TypeReceiveRequestStatus],
        children: RECEIVE_REQUEST_STATUS[value as TypeReceiveRequestStatus],
      }),
    },
    {
      field: 'warehouseName',
      headerName: '입고 센터',
      width: 120,
    },
    {
      field: 'xdockYn',
      headerName: '입고대행서비스',
      width: 110,
      valueGetter: ({ data }: ValueGetterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['xdockYn']>) => {
        if (!data) {
          return '-';
        }
        return data.xdockYn === true ? '이용함' : '이용안함';
      },
    },
    {
      field: 'releaseCode',
      headerName: '입고 방법',
      valueGetter: ({ data }: ValueGetterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['releaseCode']>) => {
        if (!data) {
          return '-';
        }
        return RELEASE_CODE[data?.releaseCode as keyof typeof RELEASE_CODE] ?? '-';
      },
      width: 90,
    },
    {
      field: 'receivingExpectedDate',
      headerName: '입고예정일시',
      valueGetter: ({
        data,
      }: ValueGetterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['receivingExpectedDate']>) => {
        if (!data) {
          return '-';
        }
        return formatReceiveExpectedDateTime(data.receivingExpectedDate, data.receivingExpectedTime);
      },
      width: 200,
    },
    {
      headerName: '',
      width: 80,
      minWidth: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: ({ data }: ICellRendererParams<TypeReceiveRequestsInfo>) => onClickViewDetail(data),
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerComponent: ReceiveSupplierHeader,
      cellRenderer: ReceiveSupplierCell<TypeReceiveRequestsInfo>,
      minWidth: 240,
      flex: 1,
    },
    {
      field: 'skuCnt',
      headerName: '품목',
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['skuCnt']>) =>
        value ? formatNumber(value) : '-',
      cellStyle: { textAlign: 'right' },
      width: 80,
    },
    {
      field: 'unitQty',
      headerName: '총 수량',
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['unitQty']>) =>
        value ? formatNumber(value) : '-',
      cellStyle: { textAlign: 'right' },
      width: 80,
    },
    {
      field: 'requestedDate',
      headerName: '요청등록일시',
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveRequestsInfo, TypeReceiveRequestsInfo['requestedDate']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
      width: 160,
    },
  ];

  return { columnDefs };
};

export default getReceiveRegisterColumnDefs;
